import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import Logo from '../../Assets/nav-logo.png';
import CvitpCommunicationsHub from '../Cvitp/CvitpCommunicationsHub';
import { getApiUrl } from '../../authConfig';
import { getValidToken } from '../../utils/msalTokenUtil';
import { FaUser, FaBuilding, FaHandsHelping, FaPhoneAlt, FaLock, FaFax, FaFileInvoiceDollar, FaSignOutAlt, FaMoneyCheckAlt, FaChevronDown, FaEllipsisV, FaFileExcel, FaFileAlt, FaClipboardList, FaExclamationTriangle } from 'react-icons/fa';

const NAV_ITEMS = [
  { to: '/landing', label: 'Due Items', shortLabel: 'Due Items', icon: FaClipboardList, match: (p) => p === '/landing' || p === '/due-items' || p === '/' },
  { to: '/personalTax', label: 'Personal Tax', shortLabel: 'Personal', icon: FaUser, match: (p) => p === '/personalTax' || p === '/personal-tax' },
  { to: '/corporateData', label: 'Corporate Tax', shortLabel: 'Corporate', icon: FaBuilding, match: (p) => p === '/corporateData' },
  { to: '/cvitp', label: 'CVITP', shortLabel: 'CVITP', icon: FaHandsHelping, match: (p) => p === '/cvitp' },
];

const ALL_NAV_ITEMS = [
  ...NAV_ITEMS,
  { to: '/fax-dashboard', label: 'Fax Dashboard', icon: FaFax, match: (p) => p.startsWith('/fax') },
  { to: '/invoice', label: 'Invoice System', icon: FaFileInvoiceDollar, match: (p) => p === '/invoice' },
  { to: '/bank-statement', label: 'Bank Statement', icon: FaFileExcel, match: (p) => p === '/bank-statement' },
  { to: '/letterhead', label: 'Letterhead', icon: FaFileAlt, match: (p) => p === '/letterhead' },
];

const MORE_ITEMS = [
  { to: '/fax-dashboard', label: 'Fax Dashboard', icon: FaFax, external: false },
  { to: '/invoice', label: 'Invoice System', icon: FaFileInvoiceDollar, external: false },
  { to: '/bank-statement', label: 'Bank Statement', icon: FaFileExcel, external: false },
  { to: 'https://portal.orientalpay.ca', label: 'Payroll', icon: FaMoneyCheckAlt, external: true },
  { to: '/letterhead', label: 'Letterhead', icon: FaFileAlt, external: false },
];

const getInitials = (name) => (name ? name.split('@')[0].substring(0, 2).toUpperCase() : 'U');

const AccountMenu = ({ displayUserName, rawUserName, onLogout, size = 'md', align = 'end' }) => {
  const dims = size === 'sm' ? 28 : 36;
  const fontSize = size === 'sm' ? 11 : 13;

  if (size === 'sm') {
    return (
      <div className="dropdown dropup bottom-nav-slot d-flex justify-content-center">
        <button
          className="bottom-nav-item-premium w-100"
          type="button"
          data-bs-toggle="dropdown"
          aria-expanded="false"
          aria-label={`Account menu for ${displayUserName}`}
        >
          <div className="bottom-nav-icon-wrapper">
            <div className="user-avatar-premium" style={{ width: dims, height: dims, fontSize }}>
              {getInitials(displayUserName)}
            </div>
          </div>
          <span className="bottom-nav-label">Account</span>
        </button>
        <ul className={`dropdown-menu dropdown-menu-${align} premium-dropdown-menu mb-2`}>
          <li className="px-3 py-3 border-bottom mb-2 account-card">
            <div className="fw-bold text-dark">{displayUserName}</div>
            <div className="small text-muted text-truncate" style={{ maxWidth: '200px' }}>{rawUserName}</div>
          </li>
          <li className="px-2">
            <button className="dropdown-item signout-item py-2 fw-medium d-flex align-items-center gap-2 rounded" onClick={onLogout}>
              <FaSignOutAlt /> Sign Out
            </button>
          </li>
        </ul>
      </div>
    );
  }

  return (
    <div className="dropdown">
      <button
        className="btn p-0 border-0 shadow-none d-flex align-items-center gap-2 account-trigger"
        data-bs-toggle="dropdown"
        aria-expanded="false"
        aria-label={`Account menu for ${displayUserName}`}
      >
        <div className="user-avatar-premium" style={{ width: dims, height: dims, fontSize }}>
          {getInitials(displayUserName)}
        </div>
        <span className="fw-semibold text-secondary d-none d-lg-inline account-name">{displayUserName}</span>
      </button>
      <ul className={`dropdown-menu dropdown-menu-${align} premium-dropdown-menu mt-2`}>
        <li className="px-3 py-3 border-bottom mb-2 account-card">
          <div className="fw-bold text-dark">{displayUserName}</div>
          <div className="small text-muted text-truncate" style={{ maxWidth: '200px' }}>{rawUserName}</div>
        </li>
        <li className="px-2">
          <button className="dropdown-item signout-item py-2 fw-medium d-flex align-items-center gap-2 rounded" onClick={onLogout}>
            <FaSignOutAlt /> Sign Out
          </button>
        </li>
      </ul>
    </div>
  );
};

const MoreMenu = ({ variant = 'desktop', location }) => (
  <ul className="dropdown-menu premium-dropdown-menu mt-2">
    {variant === 'mobile' && (
      <li className="px-3 pt-2 pb-1"><h6 className="dropdown-header px-0 more-menu-heading">Tools</h6></li>
    )}
    {MORE_ITEMS.map((item) => {
      const isMoreLinkActive = !item.external && location.pathname.startsWith(item.to.split('-')[0]);

      const Icon = item.icon;
      const content = (
        <>
          <Icon className="me-3 more-item-icon" /> {item.label}
        </>
      );
      return (
        <li className="px-2" key={item.label}>
          {item.external ? (
            <a
              className={`dropdown-item ${variant === 'mobile' ? 'py-3 fw-semibold fs-6' : 'py-2 fw-medium'} d-flex align-items-center rounded`}
              href={item.to}
              target="_blank"
              rel="noopener noreferrer"
            >
              {content}
            </a>
          ) : (
            <Link
              className={`dropdown-item ${variant === 'mobile' ? 'py-3 fw-semibold fs-6' : 'py-2 fw-medium'} d-flex align-items-center rounded ${isMoreLinkActive ? 'active' : ''}`}
              to={item.to}
            >
              {content}
            </Link>
          )}
        </li>
      );
    })}
  </ul>
);

const DataPageHeader = ({ title, description, account, onLogin, onLogout, msalInstance, isInitialized, taxEntries, dialNumber, setDialNumber, refreshTrigger, isDialerOpen, setIsDialerOpen, theme }) => {
  const location = useLocation();

  const activeNavItem = ALL_NAV_ITEMS.find(item => item.match(location.pathname));
  const TitleIcon = activeNavItem ? activeNavItem.icon : null;
  const titleColor = theme === 'glass' ? '#111827' : (theme || '#101828');
  const isMoreItemActive = activeNavItem && !NAV_ITEMS.some(navItem => navItem.to === activeNavItem.to);


  const rawUserName = account?.username || account?.name || 'User';
  const displayUserName = account?.name || account?.username || 'User';

  const [pastDueCount, setPastDueCount] = useState(0);
  const [pastDueNames, setPastDueNames] = useState([]);

  useEffect(() => {
    if (!account || !isInitialized) return;
    let isMounted = true;
    const fetchPastDue = async () => {
      try {
        const token = await getValidToken(msalInstance, account);
        const res = await fetch(getApiUrl('/v1/action-items'), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok && isMounted) {
          const items = await res.json();
          const today = new Date().toISOString().split('T')[0];
          const overdue = items.filter(i => i.due_date < today && i.status !== 'Completed');
          setPastDueCount(overdue.length);
          const names = Array.from(new Set(overdue.map(i => i.customer_name).filter(Boolean)));
          setPastDueNames(names);
        }
      } catch (err) {
        // silent fail
      }
    };
    fetchPastDue();
    const interval = setInterval(fetchPastDue, 30000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [account, isInitialized, msalInstance]);

  return (
    <>
      <style>{`
        /* --- DESKTOP HEADER --- */
        @media print { .header-card-premium, .mobile-bottom-nav-premium, .dialer-offcanvas { display: none !important; } }

        .header-card-premium {
          position: relative;
          z-index: 1040;
          background: #ffffff;
          border: 1px solid #e7eaf0;
          border-radius: 1.1rem;
          padding: 1rem 1.75rem 1.1rem;
          box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04), 0 8px 24px -12px rgba(16, 24, 40, 0.10);
          margin-bottom: 1.75rem;
        }

        .header-top-row {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: space-between;
          gap: 0.75rem;
          position: relative;
        }

        .header-brand {
          align-self: stretch;
          display: flex;
          align-items: center;
        }

        .header-brand img {
          height: 58px;
          filter: none;
        }

        @media (max-width: 991.98px) {
          .header-brand img { height: 44px; }
        }

        /* --- NEW SECOND ROW TITLE STYLES --- */
        .header-title-row {
          display: flex;
          justify-content: center;
          align-items: center;
          margin-top: 1.1rem;
          padding-top: 1.1rem;
          border-top: 1px solid #e7eaf0;
          width: 100%;
        }

        .header-title {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.6rem;
          font-weight: 700;
          font-size: 1.35rem;
          letter-spacing: -0.01em;
        }

        .header-title .title-accent {
          width: 5px;
          height: 22px;
          border-radius: 3px;
          display: inline-block;
        }

        .user-avatar-premium {
          background: #2454ad;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          border: 2px solid #ffffff;
          box-shadow: 0 0 0 1px #e7eaf0;
          flex-shrink: 0;
        }

        .account-trigger {
          border-radius: 50px;
          padding: 4px 6px 4px 4px !important;
          transition: background 0.15s ease;
        }
        .account-trigger:hover { background: #f3f5f8; }
        .account-trigger:focus-visible {
          outline: 2px solid #2454ad;
          outline-offset: 2px;
        }

        .account-name { font-size: 13.5px; }

        .account-card { background: #f8f9fb; border-radius: 0.6rem 0.6rem 0 0; }

        .signout-item { color: #c0362c; }
        .signout-item:hover, .signout-item:focus { background: #fdeeed; color: #c0362c; }

        .dialer-btn-premium {
          border-radius: 50px;
          padding: 8px 22px;
          font-size: 13.5px;
          font-weight: 600;
          border: none;
          color: #fff;
          transition: filter 0.15s ease, transform 0.15s ease;
        }
        .dialer-btn-premium.is-open { background: #c0362c; }
        .dialer-btn-premium.is-closed { background: #0f8a72; }
        .dialer-btn-premium:hover { filter: brightness(1.06); }
        .dialer-btn-premium:active { transform: scale(0.98); }
        .dialer-btn-premium:focus-visible { outline: 2px solid #101828; outline-offset: 2px; }

        .signin-btn-premium {
          background: #2454ad;
          border: none;
          color: #fff;
          font-weight: 600;
          border-radius: 50px;
          padding: 8px 22px;
          transition: filter 0.15s ease;
        }
        .signin-btn-premium:hover { filter: brightness(1.08); color: #fff; }
        .signin-btn-premium:focus-visible { outline: 2px solid #101828; outline-offset: 2px; }

        /* --- DESKTOP NAV LINKS --- */
        .header-nav-row {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 6px;
          flex: 1 1 auto;
        }

        .nav-link-premium {
          display: flex;
          align-items: center;
          gap: 9px;
          padding: 9px 20px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 13.5px;
          text-decoration: none;
          transition: background 0.15s ease, color 0.15s ease;
        }
        .nav-link-premium:focus-visible { outline: 2px solid #2454ad; outline-offset: 2px; }

        .nav-link-premium.active {
          color: #1c3f80;
          background: #eaf1fb;
        }

        .nav-link-premium.inactive {
          color: #5b6472;
        }
        .nav-link-premium.inactive:hover {
          color: #1c3f80;
          background: #f3f5f8;
        }

        .more-trigger {
          color: #5b6472;
          font-weight: 600;
          font-size: 13.5px;
        }
        .more-trigger:hover { color: #1c3f80; background: #f3f5f8; }
        .more-trigger.active {
          color: #1c3f80;
          background: #eaf1fb;
        }

        .premium-dropdown-menu {
          --bs-dropdown-link-active-bg: #eaf1fb;
          --bs-dropdown-link-active-color: #1c3f80;
        }

        .premium-dropdown-menu {
          background: #ffffff;
          border: 1px solid #e7eaf0;
          box-shadow: 0 12px 28px rgba(16, 24, 40, 0.12);
          border-radius: 0.75rem;
          padding: 0.4rem;
        }

        .more-menu-heading { font-size: 12px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; }
        .more-item-icon { color: #2454ad; opacity: 0.85; }

        .dropdown-item:hover, .dropdown-item:focus { background: #f3f5f8; }

        /* --- MOBILE BOTTOM NAV --- */
        .mobile-bottom-nav-premium {
          position: fixed;
          bottom: 12px;
          left: 10px;
          right: 10px;
          background: #ffffff;
          box-shadow: 0 6px 24px rgba(16, 24, 40, 0.14), 0 1px 3px rgba(16, 24, 40, 0.08);
          border: 1px solid #e7eaf0;
          border-radius: 20px;
          display: flex;
          justify-content: space-around;
          align-items: center;
          padding: 4px 6px;
          z-index: 1050;
        }

        .mobile-bottom-nav-premium > * {
          flex: 1 1 0% !important;
          min-width: 0 !important;
          max-width: 100% !important;
        }

        .bottom-nav-item-premium {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: #8b93a1;
          text-decoration: none;
          font-size: 10px;
          font-weight: 600;
          gap: 2px;
          width: 100%;
          min-width: 0;
          background: transparent;
          border: none;
          padding: 3px 2px;
          transition: all 0.15s ease;
        }
        .bottom-nav-item-premium:focus-visible { outline: 2px solid #2454ad; outline-offset: 2px; border-radius: 12px; }

        .bottom-nav-label {
          display: block;
          width: 100%;
          max-width: 100%;
          text-align: center;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          font-size: 10px;
          line-height: 1.15;
          letter-spacing: -0.2px;
          color: inherit;
        }

        .bottom-nav-icon-wrapper {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 16px;
          font-size: 15px;
          flex-shrink: 0;
        }

        .bottom-nav-item-premium.active .bottom-nav-icon-wrapper {
          background: #eaf1fb;
          color: #1c3f80;
        }
        .bottom-nav-item-premium.active .bottom-nav-label {
          color: #1c3f80;
          font-weight: 700;
        }

        @media (max-width: 480px) {
          .mobile-bottom-nav-premium {
            bottom: 10px;
            left: 8px;
            right: 8px;
            padding: 4px 4px;
            border-radius: 18px;
          }
          .bottom-nav-icon-wrapper {
            width: 30px;
            height: 30px;
            font-size: 14px;
            border-radius: 15px;
          }
          .bottom-nav-label {
            font-size: 9.5px;
            letter-spacing: -0.3px;
          }
        }

        @media (max-width: 380px) {
          .mobile-bottom-nav-premium {
            bottom: 8px;
            left: 6px;
            right: 6px;
            padding: 3px 2px;
          }
          .bottom-nav-icon-wrapper {
            width: 27px;
            height: 27px;
            font-size: 13px;
          }
          .bottom-nav-label {
            font-size: 8.5px;
            letter-spacing: -0.4px;
          }
        }

        @media (max-width: 991.98px) {
          body { padding-bottom: 96px !important; }
          .portal-content-container,
          .page-with-dialer {
            padding-bottom: 96px !important;
          }
          .header-card-premium { padding: 0.9rem 1rem; border-radius: 1rem; margin-bottom: 1rem; }
          .header-top-row {
            flex-wrap: nowrap;
            gap: 1rem;
          }
          .header-title-row {
             margin-top: 0.85rem;
             padding-top: 0.85rem;
          }
          .desktop-only { display: none !important; }
        }

        @media (min-width: 992px) {
          .mobile-only { display: none !important; }
        }

        @media (prefers-reduced-motion: reduce) {
          .dialer-btn-premium, .signin-btn-premium, .account-trigger { transition: none; }
        }
      `}</style>

      <header className="header-card-premium shadow-sm">
        <div className="header-top-row">
          <div className="header-brand d-flex align-items-center" style={{ zIndex: 1 }}>
            <Link to="/" className="text-decoration-none">
              <img src={Logo} alt="OBS Logo" />
            </Link>
          </div>

          <div className="d-none d-lg-flex header-nav-row">
            {NAV_ITEMS.map(({ to, label, icon: Icon, match }) => (
              <Link key={to} to={to} className={`nav-link-premium ${match(location.pathname) ? 'active' : 'inactive'}`}>
                <Icon size={14} /> {label}
                {(to === '/landing' || to === '/due-items') && pastDueCount > 0 && (
                  <span
                    className="badge rounded-pill bg-danger text-white ms-1 shadow-sm"
                    style={{ fontSize: '0.68rem', padding: '2px 6px' }}
                    title={`${pastDueCount} action items past due!`}
                  >
                    {pastDueCount}
                  </span>
                )}
              </Link>
            ))}

            <div className="dropdown ms-2">
              <button className={`btn btn-link text-decoration-none more-trigger border-0 d-flex align-items-center gap-2 px-3 py-2 rounded ${isMoreItemActive ? 'active' : ''}`} type="button" data-bs-toggle="dropdown" aria-expanded="false">
                More <FaChevronDown size={10} style={{ opacity: 0.7 }} />
              </button>
              <MoreMenu variant="desktop" location={location} />
            </div>
          </div>

          <div className="action-block d-flex align-items-center justify-content-end gap-3" style={{ zIndex: 1 }}>
            {account ? (
              <div className="d-flex align-items-center gap-3">
                {setIsDialerOpen && (
                  <button
                    className="btn dialer-btn-premium is-closed d-flex align-items-center justify-content-center"
                    onClick={() => setIsDialerOpen(true)}
                    aria-label="Open Dialer"
                    title="Open Dialer"
                  >
                    <span className="mobile-only d-flex align-items-center justify-content-center"><FaPhoneAlt size={15} /></span>
                    <span className="desktop-only d-flex align-items-center gap-2">
                      <FaPhoneAlt size={13} />
                      <span>Dialer</span>
                    </span>
                  </button>
                )}

                <div className="desktop-only">
                  <AccountMenu displayUserName={displayUserName} rawUserName={rawUserName} onLogout={onLogout} size="md" align="end" />
                </div>
              </div>
            ) : (
              <button className="btn signin-btn-premium" onClick={onLogin}>
                <span className="mobile-only"><FaLock size={14} /></span>
                <span className="desktop-only">Sign In</span>
              </button>
            )}
          </div>
        </div>

        {/* Second Row: Centered Themed Title */}
        {title && (
          <div className="header-title-row">
            <div className="header-title" style={{ color: titleColor }}>
              {TitleIcon && <TitleIcon style={{ marginRight: '10px', opacity: 0.9 }} />}
              <span style={{ fontWeight: 700, letterSpacing: '-0.5px' }}>
                {title}
              </span>
            </div>
          </div>
        )}

        {/* Past Due Notification Alert Banner - Integrated into header across all tabs */}
        {pastDueCount > 0 && (
          <div
            className="mt-3 p-2 px-3 rounded-3 border d-flex align-items-center justify-content-between flex-wrap gap-2 shadow-sm"
            style={{
              background: '#fef2f2',
              borderColor: '#fca5a5',
              transition: 'all 0.2s ease',
            }}
            role="alert"
          >
            <div className="d-flex align-items-center gap-2">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                style={{ width: 28, height: 28, background: '#fee2e2', color: '#dc2626' }}
              >
                <FaExclamationTriangle size={14} />
              </div>
              <div style={{ fontSize: '0.85rem' }}>
                <span className="fw-bold text-danger">Past Due Alert:</span>{' '}
                <span className="text-dark fw-medium">
                  {pastDueCount} {pastDueCount === 1 ? 'action item is' : 'action items are'} overdue!
                </span>
                {pastDueNames.length > 0 && (
                  <span className="text-muted ms-1 d-none d-md-inline" style={{ fontSize: '0.8rem' }}>
                    ({pastDueNames.slice(0, 3).join(', ')}{pastDueNames.length > 3 ? `, +${pastDueNames.length - 3} more` : ''})
                  </span>
                )}
              </div>
            </div>
            <Link
              to="/landing"
              className="btn btn-sm btn-danger fw-semibold d-flex align-items-center gap-1 shadow-sm py-1 px-3 text-decoration-none"
              style={{ fontSize: '0.78rem' }}
            >
              <FaClipboardList size={11} /> View Overdue Items &rarr;
            </Link>
          </div>
        )}

        {setIsDialerOpen && account && (
          ReactDOM.createPortal(
            <CvitpCommunicationsHub
              account={account}
              msalInstance={msalInstance}
              isInitialized={isInitialized}
              taxEntries={taxEntries || []}
              dialNumber={dialNumber}
              setDialNumber={setDialNumber}
              refreshTrigger={refreshTrigger}
              isDialerOpen={isDialerOpen}
              setIsDialerOpen={setIsDialerOpen}
            />,
            document.body
          )
        )}
      </header>

      <nav className="mobile-bottom-nav-premium mobile-only" aria-label="Primary">
        {NAV_ITEMS.map(({ to, shortLabel, icon: Icon, match }) => (
          <Link key={to} to={to} className={`bottom-nav-item-premium position-relative ${match(location.pathname) ? 'active' : ''}`}>
            <div className="bottom-nav-icon-wrapper position-relative">
              <Icon />
              {(to === '/landing' || to === '/due-items') && pastDueCount > 0 && (
                <span
                  className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger text-white"
                  style={{ fontSize: '0.58rem', padding: '2px 5px' }}
                >
                  {pastDueCount}
                </span>
              )}
            </div>
            <span className="bottom-nav-label">{shortLabel}</span>
          </Link>
        ))}

        <div className="dropdown dropup bottom-nav-slot d-flex justify-content-center">
          <button className={`bottom-nav-item-premium w-100 ${isMoreItemActive ? 'active' : ''}`} type="button" data-bs-toggle="dropdown" aria-expanded="false">
            <div className="bottom-nav-icon-wrapper"><FaEllipsisV /></div>
            <span className="bottom-nav-label">More</span>
          </button>
          <MoreMenu variant="mobile" location={location} />
        </div>

        {account ? (
          <AccountMenu displayUserName={displayUserName} rawUserName={rawUserName} onLogout={onLogout} size="sm" align="end" />
        ) : (
          <div className="bottom-nav-slot d-flex justify-content-center">
            <button className="bottom-nav-item-premium w-100" onClick={onLogin}>
              <div className="bottom-nav-icon-wrapper" style={{ color: '#2454ad' }}><FaLock /></div>
              <span className="bottom-nav-label" style={{ color: '#2454ad' }}>Sign In</span>
            </button>
          </div>
        )}
      </nav>
    </>
  );
};

export default DataPageHeader;